import { useState } from 'react'
import { X, Upload, SignalLow, SignalMedium, SignalHigh } from 'lucide-react'

export interface TicketFormData {
  subject: string
  priority: 'low' | 'medium' | 'high'
  description: string
  images?: File[]
}

interface CreateTicketModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (ticketData: TicketFormData) => Promise<void> | void
  isSubmitting?: boolean
}

export const CreateTicketModal = ({ isOpen, onClose, onSubmit, isSubmitting = false }: CreateTicketModalProps) => {
  const [subject, setSubject] = useState('')
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [description, setDescription] = useState('')
  const [images, setImages] = useState<File[]>([])
  const maxChars = 300

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await Promise.resolve(onSubmit({
        subject,
        priority,
        description,
        images
      }))
      // Reset form after successful submission
      setSubject('')
      setPriority('medium')
      setDescription('')
      setImages([])
    } catch (error) {
      // Surface error via parent handler; keep modal state intact
      console.error('CreateTicketModal: failed to submit ticket', error)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setImages(Array.from(e.target.files))
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Create New Ticket</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-900 mb-2">
              Subject
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Backup failure"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              required
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Priority
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPriority('low')}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  priority === 'low'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 border border-[#E8E8E8] hover:bg-gray-200'
                }`}
              >
                Low <SignalLow className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setPriority('medium')}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  priority === 'medium'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 border border-[#E8E8E8] hover:bg-gray-200'
                }`}
              >
                Medium <SignalMedium className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setPriority('high')}
                className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  priority === 'high'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 border border-[#E8E8E8] hover:bg-gray-200'
                }`}
              >
                High <SignalHigh className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-900 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, maxChars))}
              placeholder="Describe the issue in detail. Include any error messages, steps to reproduce, and affected devices."
              rows={6}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
              required
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-500">{description.length}/{maxChars}</span>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Image
            </label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-gray-300 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  Upload Snapshots & Screenshots of the issue
                </span>
                {images.length > 0 && (
                  <span className="text-xs text-gray-600 mt-2">
                    {images.length} file(s) selected
                  </span>
                )}
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-gray-900 text-white py-4 rounded-full font-medium hover:bg-gray-800 transition-colors"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Issue'}
          </button>
        </form>
      </div>
    </div>
  )
}
